import React, { useState } from "react";
import useAxios from "axios-hooks";
import { toast } from "react-toastify";
import { Input, Button } from "semantic-ui-react";

import { ControlButtons } from "components/shared";
import { LIVE_CORE_COMMANDS } from "../../constants";

// Schema-driven turbines (MQTT bridge) don't have a "Command" sheet with a
// permission tier per row - all their commands are coils on the
// TurbineSchemaRegister. The Live tab only shows the curated
// LIVE_CORE_COMMANDS subset; everything else (resets, restart controller,
// vib sensor update, etc.) lands here.
//
// Vestas is the exception (2026-09-23 request, corrected same day): it's a
// real, live turbine, not a simulator/test rig like the others, but its
// commands live in the Live tab's "CONTROLS" section instead (see
// components/live/LiveControlButtons.js) - so none of its commands are
// duplicated here. Power Limit (below) is the one control that lives here
// instead, since it needs a value input, not a one-click action.
const SchemaAdvancedControlButtons = ({ windTurbineIdentifier, schemaId }) => {
  const [{ data: coils }] = useAxios({
    url: "/turbineSchemaRegisters",
    params: {
      filter: JSON.stringify({ schemaId, registerType: "coil" }),
    },
  });

  const isVestas = schemaId === "vestas";
  const buttons = isVestas
    ? []
    : (coils || [])
        .filter((r) => !LIVE_CORE_COMMANDS.includes(r.dbname))
        .map((r) => ({ dbname: r.dbname, function: r.dbname }));

  return (
    <ControlButtons
      windTurbineIdentifier={windTurbineIdentifier}
      buttons={buttons}
      useMqttCommand
    />
  );
};

// set_power_limit/clear_power_limit (PF2 V0.9.39+, see cei016_logic.cpp's
// evalMqttPowerLimit()) clamp finalPowerCmdKw down, but only once it's
// already above the set limit - same clamp-only-when-exceeded behavior as
// the existing OV/DSO limit stages. Independent of the physical CCI's own
// DSO limit, so it works whether or not a CCI is actually connected.
// Doesn't reuse ControlButtons/ExecuteActionButton since those only ever
// send a fixed value (1) - this needs an operator-entered kW value.
const PowerLimitControl = ({ windTurbineId }) => {
  const [value, setValue] = useState("");
  const [{ loading }, sendCommand] = useAxios(
    { url: `/windTurbines/${windTurbineId}/mqttCommand`, method: "PUT" },
    { manual: true }
  );

  const send = async (tag, v) => {
    try {
      await sendCommand({ data: { tag, value: v } });
      toast.success(`TMC - ${tag} executed successfully`, { hideProgressBar: true });
    } catch (err) {
      const serverMessage = err && err.response && err.response.data && err.response.data.error;
      toast.error(serverMessage || `TMC - Failed executing ${tag}`, { hideProgressBar: true });
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
      <Input
        type="number"
        placeholder="kW"
        value={value}
        onChange={(e, { value: v }) => setValue(v)}
      />
      <Button
        disabled={loading || value === ""}
        onClick={() => send("set_power_limit", parseFloat(value))}
      >
        Set Power Limit
      </Button>
      <Button disabled={loading} onClick={() => send("clear_power_limit", 0)}>
        Clear Power Limit
      </Button>
    </div>
  );
};

const AUTO_RESET_RESULT_TEXT = {
  running: "running",
  ok: "turbine running again",
  skipped_limit: "not reset - limit reached",
  failed_reset_write: "failed (reset coil write)",
  failed_reset_release: "failed (reset coil release)",
  failed_start_write: "failed (start coil write)",
};

const describeAutoResetResult = (r) => {
  if (!r) return "";
  if (AUTO_RESET_RESULT_TEXT[r]) return AUTO_RESET_RESULT_TEXT[r];
  if (r.startsWith("starting:")) return `turbine starting (state ${r.slice(9)})`;
  if (r.startsWith("still:")) return `error still present (state ${r.slice(6)})`;
  return r;
};

const formatAgo = (s) => {
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
};

// Bridge 0.6.8+ automatic fault reset (schema "autoReset" block, e.g. RAGO):
// same sequence as the ModbusRagoOK8 tool's AutoReset, run by the bridge
// itself. Shown only when the bridge reports it (deviceInfo.autoReset).
const AutoResetControl = ({ windTurbineId }) => {
  const [{ data: turbine }, refetch] = useAxios(
    { url: `/windTurbines/${windTurbineId}` },
    { useCache: false }
  );
  const [{ loading }, sendCommand] = useAxios(
    { url: `/windTurbines/${windTurbineId}/mqttCommand`, method: "PUT" },
    { manual: true }
  );
  React.useEffect(() => {
    const id = setInterval(() => refetch().catch(() => {}), 10000);
    return () => clearInterval(id);
  }, [refetch]);

  const ar = turbine && turbine.deviceInfo && turbine.deviceInfo.autoReset;
  if (!ar) return null;

  const send = async (tag) => {
    try {
      await sendCommand({ data: { tag, value: 1 } });
      toast.success(tag === "autoreset_on" ? "Auto-reset enabled" : "Auto-reset disabled", { hideProgressBar: true });
      setTimeout(() => refetch().catch(() => {}), 1500);
    } catch (err) {
      const serverMessage = err && err.response && err.response.data && err.response.data.error;
      toast.error(serverMessage || `Failed: ${tag}`, { hideProgressBar: true });
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong>Auto-reset:</strong>
        <span style={{ color: ar.enabled ? "#1e8e3e" : "gray", fontWeight: 700 }}>
          {ar.enabled ? (ar.running ? "ON - resetting now" : "ON") : "OFF"}
        </span>
        <Button disabled={loading || ar.enabled} onClick={() => send("autoreset_on")}>
          Auto-reset ON
        </Button>
        <Button disabled={loading || !ar.enabled} onClick={() => send("autoreset_off")}>
          Auto-reset OFF
        </Button>
      </div>
      <div style={{ fontSize: "0.85rem", color: "gray", marginTop: 6 }}>
        {typeof ar.gridCount1h === "number" &&
          `Grid errors: ${ar.gridCount1h}/${ar.gridMaxPerHour} last hour, ${ar.gridCount24h}/${ar.gridMaxPerDay} last 24 h · `}
        Turbine errors: {ar.count1h}/{ar.maxPerHour} last hour, {ar.count24h}/{ar.maxPerDay} last 24 h
        {ar.retryS ? ` · retry every ${ar.retryS}s while the error persists` : ""}
        {ar.limitHit ? " - limit reached, paused until it frees up" : ""}
        {typeof ar.lastCode === "number" &&
          ` · Last: error ${ar.lastCode}, ${describeAutoResetResult(ar.lastResult)}` +
            (typeof ar.lastAgoS === "number" ? `, ${formatAgo(ar.lastAgoS)}` : "")}
      </div>
    </div>
  );
};

const AdvancedControlButtons = (props) => {
  const schemaId = props.windTurbine.type && props.windTurbine.type.schemaId;

  if (schemaId) {
    return (
      <>
        <SchemaAdvancedControlButtons
          windTurbineIdentifier={props.windTurbine.identifier}
          schemaId={schemaId}
        />
        {schemaId === "vestas" && (
          <PowerLimitControl windTurbineId={props.windTurbine.id} />
        )}
        {schemaId !== "vestas" && <AutoResetControl windTurbineId={props.windTurbine.id} />}
      </>
    );
  }

  return (
    <ControlButtons
      windTurbineIdentifier={props.windTurbine.identifier}
      buttons={
        props.turbineParams
          ? props.turbineParams.Command.filter(
              (b) => b.dbname && b.permission === 1
            )
          : []
      }
    ></ControlButtons>
  );
};

AdvancedControlButtons.propTypes = {};

AdvancedControlButtons.defaultProps = {};

export default AdvancedControlButtons;
